/**
 * Public runtime configuration. The SPA holds PUBLIC values only — never a BF API credential, a bearer
 * token, an OAuth secret, or the reCAPTCHA secret key (SAD-001 P-01, P-03).
 */
interface PublicConfig {
  readonly apiBaseUrl: string;
  readonly entraClientId: string;
  readonly entraTenantId: string;
  readonly recaptchaSiteKey: string;
  /** Master switch for reCAPTCHA v3 in the SPA. Off means no script is loaded and no token is sent. */
  readonly recaptchaEnabled: boolean;
  /** Minutes of inactivity after which the provider's session is ended (US-003 AC-001). */
  readonly sessionTimeoutMinutes: number;
  /** Minutes left on the session at which the "Session Expiring Soon" dialog appears. 0 disables it. */
  readonly sessionWarningMinutes: number;
  /** External Privacy Policy page. */
  readonly privacyPolicyUrl: string;
  /** External Terms & Conditions page. */
  readonly termsUrl: string;
  /** URL of the Provider Update Form PDF (US-017). Empty means the Forms tab shows its fallback. */
  readonly providerUpdateFormUrl: string;
}

const DEFAULT_SESSION_TIMEOUT_MINUTES = 20;
const DEFAULT_SESSION_WARNING_MINUTES = 5;
const DEFAULT_PRIVACY_POLICY_URL = 'https://bankersfidelity.com/privacypolicy/';
const DEFAULT_TERMS_URL = 'https://bankersfidelity.com/termsofuse/';

declare global {
  interface Window {
    __PORTAL_CONFIG__?: Record<string, string>;
  }
}

/**
 * Reads a public value, preferring the one the container wrote at start-up.
 *
 * Vite inlines `import.meta.env` at BUILD time, so a value baked into the image cannot differ between
 * environments — and the same image is promoted unchanged from dev to prod (SAD-001 P-11). The
 * container entrypoint writes /config.js instead; the build-time value remains the fallback, which is
 * what makes `npm run dev` work from .env.local.
 */
function read(key: string): string {
  const runtime = window.__PORTAL_CONFIG__?.[key];
  if (runtime !== undefined && runtime.length > 0) {
    return runtime;
  }

  const value = import.meta.env[key] as string | undefined;
  return value ?? '';
}

/**
 * Reads a boolean flag. Defaults to ON: a missing or misspelled value must never be what silently
 * switches a security control off. Only an explicit "false" or "0" disables it.
 */
function readFlag(key: string): boolean {
  const value = read(key).trim().toLowerCase();
  return value !== 'false' && value !== '0';
}

/**
 * Reads a positive whole number of minutes. A missing, malformed or non-positive value falls back to the
 * default rather than disabling the timeout: a typo in configuration must not leave sessions open
 * forever.
 */
function readTimeoutMinutes(key: string, fallback: number): number {
  const parsed = Number(read(key));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Like {@link readTimeoutMinutes} but 0 is meaningful: it switches the warning off. */
function readWarningMinutes(key: string, fallback: number): number {
  const raw = read(key).trim();
  const parsed = Number(raw);
  return raw.length > 0 && Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export const config: PublicConfig = {
  apiBaseUrl: read('VITE_API_BASE_URL'),
  entraClientId: read('VITE_ENTRA_CLIENT_ID'),
  entraTenantId: read('VITE_ENTRA_TENANT_ID'),
  recaptchaSiteKey: read('VITE_RECAPTCHA_SITE_KEY'),
  recaptchaEnabled: readFlag('VITE_RECAPTCHA_ENABLED'),
  providerUpdateFormUrl: read('VITE_PROVIDER_UPDATE_FORM_URL'),
  sessionWarningMinutes: readWarningMinutes(
    'VITE_SESSION_WARNING_MINUTES',
    DEFAULT_SESSION_WARNING_MINUTES,
  ),
  privacyPolicyUrl: read('VITE_PRIVACY_POLICY_URL') || DEFAULT_PRIVACY_POLICY_URL,
  termsUrl: read('VITE_TERMS_URL') || DEFAULT_TERMS_URL,
  sessionTimeoutMinutes: readTimeoutMinutes(
    'VITE_SESSION_TIMEOUT_MINUTES',
    DEFAULT_SESSION_TIMEOUT_MINUTES,
  ),
};
